"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bulkPersonEditSchema = exports.completeOnboardingRequestSchema = exports.onboardingSkillSchema = exports.onboardingQuotaSchema = exports.onboardingScheduleSchema = exports.onboardingRateSchema = exports.setMemberRolesRequestSchema = exports.personHistoryEntrySchema = exports.setMemberStatusRequestSchema = exports.updatePersonRequestSchema = exports.personSchema = exports.MEMBER_STATUSES = exports.SENIORITY_COLORS = exports.SENIORITY_LEVELS = exports.EMPLOYMENT_TYPES = exports.BILLING_KINDS = void 0;
exports.billablePercent = billablePercent;
const zod_1 = require("zod");
const teams_1 = require("./teams");
const identifier_1 = require("../identifier");
const bulk_1 = require("../bulk");
/* ⚠️ C61 · the wizard prices a person, so it borrows C51's unit rather than declaring a second. */
const rate_card_1 = require("../workspace/rate-card");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ People — C14's wire shapes.                                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `main.jsx` 4255–5062. The mockup keeps this in `PEOPLE_EXT` beside its users; 0020 puts it on
 * `members` and says why.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * The four buckets a logged hour falls into — C63.
 *
 * ⚠️ **Three are `contract_line_items.type` and the fourth is the absence of one.** `none` is not a
 * money type and never will be; it is *"these hours reach no deliverable"*, which is a real and
 * ordinary state rather than a gap in the data.
 */
exports.BILLING_KINDS = ['fixed', 'time_and_material', 'non_billable', 'none'];
/**
 * What share of a month's hours was billable — the person detail's *Számlázható* figure.
 *
 * ⚠️⚠️ **FIXED AND TIME-AND-MATERIAL ARE BILLABLE; `non_billable` AND `none` ARE NOT.** A fixed
 * deliverable is sold for a price whether or not the hour inside it is priced separately, which is
 * why it counts — the mockup's own arithmetic, and reading it the other way round would report
 * every fixed-price project as unbillable work.
 *
 * ⚠️ **`null` when nothing was logged**, never `0`: a month nobody has worked yet has no share to
 * report, and a confident zero on the first of the month reads as a bad month rather than an empty
 * one.
 */
function billablePercent(split) {
    const total = Object.values(split).reduce((sum, hours) => sum + Number(hours), 0);
    if (total <= 0)
        return null;
    const billable = Number(split.fixed ?? 0) + Number(split.time_and_material ?? 0);
    return Math.round((billable / total) * 100);
}
/** The mockup's `EMP_TYPES`. */
exports.EMPLOYMENT_TYPES = ['internal', 'contractor'];
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ C58 · SZENIORITÁS — the level a person works at.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **NOT THE RATE CARD.** A rate card says what an hour costs and sells for, on a timeline
 * (`0127`); this says what somebody IS. They correlate and they are not the same — two seniors on
 * different cards, a medior on a rate nobody has revisited — and the finance breakdown exists to
 * show exactly that spread. Deriving one from the other would make the panel answer its own
 * question.
 *
 * ⚠️ **`null` is a real state and means nobody has said.** `0135` adds the column with no default
 * for that reason: filling one in on a migration would assert a level about every existing member.
 */
exports.SENIORITY_LEVELS = ['junior', 'medior', 'senior', 'lead', 'principal'];
/** ⚠️ The mockup's own colours, so a dot means the same thing on every screen that draws one. */
exports.SENIORITY_COLORS = {
    junior: '#8F8F9B',
    medior: '#4EA7FC',
    senior: '#209292',
    lead: '#8B5CF6',
    principal: '#E9A23B',
};
/**
 * The mockup's `PERSON_STATUS`: `active` / `invited` / `disabled`.
 *
 * ⚠️ **Ours is `deactivated`, not `disabled`, because that is what the enum in 0004 already says** —
 * and renaming a database enum to match a prototype's label is the wrong direction. The screens
 * translate it; `decisions.md` T-7 uses "deactivate" too.
 */
exports.MEMBER_STATUSES = ['invited', 'active', 'deactivated'];
/**
 * One row of the people list, and the header of the person detail.
 *
 * ⚠️ **One shape for both, rather than a `PersonRow` and a `Person`.** The list shows nine columns
 * and the detail shows the same facts as a header; two schemas would mean two reads that can
 * disagree about somebody's team. The detail asks for its tabs separately, which is where the extra
 * data actually is.
 */
exports.personSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /**
     * The public identifier, minted by the database (`0194`): `member_<8 hex>` for a member row,
     * `invitation_<8 hex>` for a pending-invitation row — `kind` says which arm this one came from.
     */
    identifier: zod_1.z.union([(0, identifier_1.identifierSchema)('member'), (0, identifier_1.identifierSchema)('invitation')]),
    displayName: zod_1.z.string(),
    email: zod_1.z.string(),
    /**
     * ⚠️ **C22 · the path to this person's picture, or null.** `/files/avatar/<user>` — a PATH the
     * server derived, which the client resolves against its own API base and reads THROUGH the
     * session, because an `<img src>` cannot carry an Authorization header.
     *
     * ⚠️ It rides beside the display name rather than being looked up per row: a list that asked
     * "does this person have a picture" once per row would answer with a 404 for everybody who does
     * not, which is a refusal per row for the ordinary case.
     */
    avatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    avatarSeed: zod_1.z.string().nullable(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE NAME'S TWO HALVES — item 6. `displayName` STAYS.             │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **BOTH NULLABLE, AND THAT IS NOT LAZINESS.** `0101` splits an existing `display_name` at
     * its first space, which is a GUESS about somebody's name — wrong for a two-word family name, a
     * name with no space, or one typed the other way round. A name that could not be split has
     * neither half, and `personName()` renders `displayName` unchanged for it. Nobody's name is
     * silently reordered.
     *
     * ⚠️⚠️ **`displayName` IS THE CANONICAL FORM AND IS NOT DERIVED ON THE CLIENT.** A trigger keeps
     * it as `family given` — the order this company writes names in, and the order a `desc` sort, a
     * CSV export and every one of the 162 existing readers see. What the READER's locale decides is
     * what is DRAWN: `Kovács Anna` in Hungarian, `Anna Kovács` in English, from the two halves.
     */
    givenName: zod_1.z.string().nullable(),
    familyName: zod_1.z.string().nullable(),
    status: zod_1.z.enum(exports.MEMBER_STATUSES),
    /**
     * ⚠️ **Null once P-2 has run, and the display name is NOT.** A tombstone keeps its name so a
     * worklog report stays readable; everything here that could contact or identify somebody is
     * cleared. A client that renders `phone ?? '—'` needs to know the difference between "never had
     * one" and "cleared", which `deletedAt` answers.
     */
    phone: zod_1.z.string().nullable(),
    employmentType: zod_1.z.enum(exports.EMPLOYMENT_TYPES),
    /** C58 · ⚠️ Null means nobody has said — see `SENIORITY_LEVELS`. */
    seniority: zod_1.z.enum(exports.SENIORITY_LEVELS).nullable(),
    /** R-60 · every team they are in, by name. Empty for an invitation and for nobody-yet. */
    teams: zod_1.z.array(teams_1.teamRefSchema),
    location: zod_1.z.string().nullable(),
    timezone: zod_1.z.string(),
    language: zod_1.z.enum(['hu', 'en']),
    /** Whether this person is expected to log hours. False keeps them out of the hour reports. */
    tracksTime: zod_1.z.boolean(),
    /**
     * C46 · whether this workspace manages their leave at all.
     *
     * ⚠️ **False means no quotas and no requests, which is not the same as an entitlement of zero.**
     * A contractor whose leave is a matter between them and their own company is the case; the quota
     * grid names them underneath rather than drawing them an empty row.
     */
    leaveManaged: zod_1.z.boolean(),
    /**
     * ux-pass-2 · *Erőforrás* — whether this person is planned as a resource (`0130`'s
     * `members.resource_planned`).
     *
     * ⚠️ **False keeps them out of the resource views and off the allocation pickers**, which is
     * not the same as `tracksTime`: somebody can log hours without anybody planning their capacity
     * (a director), and the column has existed since `0130` with nothing on the wire to change it.
     *
     * ⚠️ **U-20 · the reverse is not a state.** Somebody who does not log is never planned — turning
     * `tracksTime` off turns this off, and turning this on while it is off is `member_not_tracking`.
     */
    resourcePlanned: zod_1.z.boolean(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ 17 Oct 2026 · *Alap szerep* — the project role this person works as.      │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * `0058`'s `members.default_project_role_id`, resolved. A new project membership starts from it
     * (`project.service.ts`), and the Utilizáció dashboard groups and filters by it — so somebody who
     * is Frontend on one project and Backend on another counts once, as what they ARE.
     *
     * ⚠️ Null means nobody has said; the dashboard files them under *Nincs szerepkör*.
     */
    defaultProjectRole: zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string(), color: zod_1.z.string() }).nullable(),
    managerMemberId: zod_1.z.string().nullable(),
    managerName: zod_1.z.string().nullable(),
    startsOn: wireDateSchema.nullable(),
    endsOn: wireDateSchema.nullable(),
    /** ISO instant, or null when the seven-step wizard has not been completed. */
    onboardedAt: zod_1.z.string().nullable(),
    /** ISO instant. Non-null means P-2 has run: this row is a tombstone. */
    deletedAt: zod_1.z.string().nullable(),
    /**
     * The workspace role names this member holds, already resolved.
     *
     * ⚠️ **A LIST, because a member's effective permissions are the merge of their roles** — C5, and
     * the mockup's single `plevel` cannot express it. The list screen shows them joined; the detail
     * lets them be changed one at a time.
     */
    roles: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string(), code: zod_1.z.string() })),
    /** ⚠️ Summed in SQL. `numeric` crosses the driver as a string; render through `formatHours`. */
    monthHours: zod_1.z.string(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ C63 · *Elszámolás — e hónap*: what those hours were worth.                │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **KEYED ON THE LINE ITEM'S MONEY TYPE, PLUS `none` FOR HOURS THAT REACH NO LINE ITEM.**
     * Internal work and a task filed against the project rather than a deliverable are real hours in
     * the total, so a split over the three types alone would lose them and the percentages would not
     * add up. ⚠️ **A missing key is not zero, it is *nothing this month***: the object carries only
     * what was worked, which is what makes an empty object mean *"nothing yet"* rather than *"four
     * zeroes"*.
     *
     * ⚠️ Strings, like every other `numeric` on this wire.
     */
    /* ⚠️ `partialRecord`, NOT `record` —  records the same defect: a plain
       `z.record` over an enum types every key as REQUIRED, and this object carries only what was
       worked. */
    monthSplit: zod_1.z.partialRecord(zod_1.z.enum(exports.BILLING_KINDS), zod_1.z.string()),
    /** Projects with a membership covering **today** — the window, not a row count. */
    projectCount: zod_1.z.number().int().nonnegative(),
    /** C10's `person` metafields, resolved in. */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    version: zod_1.z.number().int(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ A ROW MAY BE A PENDING INVITATION — item 44, and this is an       │
     * │ ADDITION to the schema (CLAUDE.md rule 8), flagged as one.            │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * The tester feedback asks for invitations *in the list, with status Invited, not a separate
     * section* — and the product owner's ruling of 9 Sep 2026 added *"make sure it works across all
     * pages"*, which is what decides the implementation. Merging two arrays in the browser works on
     * page one and nowhere else, so the invitations join the people QUERY and are sorted, filtered,
     * counted and paged with everybody.
     *
     * ⚠️⚠️ **THE CODE THIS REVERSES ARGUED THE OTHER WAY AND ARGUED IT WELL.**
     * `invitations-panel.tsx` said: *"every column is empty for them — no team, no employment type,
     * no hours — and every filter has to decide what to do with a person who is not one"*. Both
     * halves are true and neither goes away; what changed is that the product owner would rather
     * have empty cells in one list than a second block below it. So the filters DO decide: naming a
     * team, an employment type or a time-tracking state excludes invitations, because an invitation
     * has none of those and pretending otherwise would answer a question with a row that cannot
     * satisfy it.
     *
     * ⚠️ **`id` is the INVITATION's id on such a row, not a member id.** Nothing that takes a member
     * id may be handed one — the screen's row click and its menu both branch on this field.
     */
    kind: zod_1.z.enum(['member', 'invitation']),
});
/**
 * ⚠️ **Every field optional but the version, because the detail edits ONE at a time.** The mockup's
 * `pxPatch(id, { team })` is the whole interaction model; sending the object back would make each
 * pick overwrite fields nobody looked at.
 */
exports.updatePersonRequestSchema = zod_1.z.object({
    /**
     * ⚠️ **Item 6 · the two halves, and NOT `displayName`.** The trigger composes that one, so
     * accepting it here would give a write two ways to say the same thing and let them disagree.
     */
    givenName: zod_1.z.string().trim().min(1).max(100).optional(),
    familyName: zod_1.z.string().trim().min(1).max(100).optional(),
    phone: zod_1.z.string().max(40).nullable().optional(),
    employmentType: zod_1.z.enum(exports.EMPLOYMENT_TYPES).optional(),
    /* C58 · ⚠️ Nullable AND optional: absent leaves it, `null` clears it back to *nobody has said*. */
    seniority: zod_1.z.enum(exports.SENIORITY_LEVELS).nullable().optional(),
    /** R-60 · the whole set — absent leaves it, `[]` takes them out of every team. */
    teamIds: teams_1.teamIdsSchema.optional(),
    location: zod_1.z.string().max(120).nullable().optional(),
    timezone: zod_1.z.string().min(1).max(64).optional(),
    language: zod_1.z.enum(['hu', 'en']).optional(),
    tracksTime: zod_1.z.boolean().optional(),
    /* ux-pass-2 · *Erőforrás* — the person screen's row and the list's row-menu toggle. */
    resourcePlanned: zod_1.z.boolean().optional(),
    /* 17 Oct 2026 · *Alap szerep*. ⚠️ Absent leaves it, `null` clears it — like `seniority`. */
    defaultProjectRoleId: zod_1.z.uuid().nullable().optional(),
    managerMemberId: zod_1.z.uuid().nullable().optional(),
    startsOn: wireDateSchema.nullable().optional(),
    endsOn: wireDateSchema.nullable().optional(),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * ⚠️ **`invited` is NOT settable, and the mockup agrees** — its status menu disables that option.
 * Being invited is a fact about an invitation that has not been accepted; setting it by hand would
 * claim an invitation nobody sent.
 */
exports.setMemberStatusRequestSchema = zod_1.z.object({
    status: zod_1.z.enum(['active', 'deactivated']),
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * One entry of the person's history tab.
 *
 * ⚠️⚠️ **DERIVED FROM `audit_log`, WHERE THE MOCKUP SYNTHESISES IT FROM CURRENT STATE.** Its
 * `personHistory` invents *"felvéve a workspace-be"* from a start date and dates a deactivation
 * "today" whenever the status happens to be disabled — so its history changes retroactively when a
 * field changes, and shows events that never happened at times they did not happen. We have the
 * actual record: actor, action, before/after, and when. **This tab will be sparse on a seeded
 * workspace, and that is correct** — nothing happened to those rows.
 */
exports.personHistoryEntrySchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `member.update`, `project.member_add`, … — a `resource.action` key the client translates. */
    action: zod_1.z.string(),
    /** The actor's display name, or null for a row whose actor is gone. */
    actorName: zod_1.z.string().nullable(),
    /** ISO instant. */
    at: zod_1.z.string(),
    /** The fields that changed, for a one-line summary. */
    changed: zod_1.z.array(zod_1.z.string()),
});
/* ── C14's role assignment and the onboarding wizard ───────────────────────────────────────── */
/**
 * ⚠️⚠️ **ASSIGNING A ROLE IS A PRIVILEGE OPERATION, AND ITS OWN ENDPOINT FOR THAT REASON.** C5 built
 * role *definitions*; nothing could put a member into one. The mockup has a permission-level picker
 * on the person detail and another in the wizard, so two callers want it — and folding it into
 * `PATCH /people/:id` would mean one endpoint enforcing two very different guards depending on
 * which key happened to arrive.
 *
 * ⚠️ **A LIST, replaced wholesale** — and since 13 Oct 2026 a list of AT MOST ONE. The product
 * owner ruled that a person has a single role (`decisions.md` R-56); the wire keeps its array so no
 * caller changes shape, and `max(1)` is what makes a second one a `validation_failed` rather than
 * a merge nobody can see on screen. Empty stays legal: it is how somebody invited before roles
 * existed reads, and the person page never offers it as a choice.
 */
exports.setMemberRolesRequestSchema = zod_1.z.object({
    roleIds: zod_1.z.array(zod_1.z.uuid()).max(1),
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * The seven-step wizard's payload — `main.jsx` 4454–4500's `obFinish`, in one request.
 *
 * ⚠️⚠️ **ONE REQUEST, NOT ONE PER STEP, AND THAT IS THE POINT OF THE ENDPOINT.** The wizard is a
 * single act: abandoning it halfway must leave nothing behind, and seven `PATCH`es cannot promise
 * that — the fourth failing would leave somebody half-onboarded with no record of which half. The
 * mockup patches everything at the end too, and it has no transaction to lose.
 *
 * ⚠️ **It stamps `onboardedAt`**, which is the only thing here the person detail cannot already
 * edit. That is what makes the wizard a distinct operation rather than a shortcut through the form.
 */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C61 · the wizard's `rate` step — the tenth in the mockup, the eighth here. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SAME TWO ARMS `saveRateAssignmentRequestSchema` HAS, WITHOUT THE `memberId`.** The
 * person is the one being onboarded — carrying their id a second time inside the body would let a
 * request name one member in the path and price another. The shape is otherwise deliberately
 * identical, because it writes into the same table through the same upsert.
 *
 * ⚠️ **`null` is *skipped*, and the wizard has a switch for it** — *"Kihagyva — a dolgozónak nem
 * lesz díja, később a Beállítások › Díjkártyák alatt pótolható."* Somebody onboarding a colleague
 * on their first morning may not know what they cost yet, and a wizard that cannot be finished
 * without a figure is one people abandon. The same argument every other field here already won.
 */
exports.onboardingRateSchema = zod_1.z.discriminatedUnion('source', [
    zod_1.z.object({
        source: zod_1.z.literal('template'),
        fromDate: wireDateSchema,
        templateId: zod_1.z.uuid(),
    }),
    zod_1.z.object({
        source: zod_1.z.literal('custom'),
        fromDate: wireDateSchema,
        unit: zod_1.z.enum(rate_card_1.RATE_UNITS),
        currencyCode: zod_1.z.string().regex(/^[A-Z]{3}$/),
        cost: zod_1.z.number().min(0),
        target: zod_1.z.number().min(0),
    }),
]);
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C64 · the wizard's `sched` step — the sixth in the mockup's order.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SAME TWO ARMS `saveWorkScheduleAssignmentRequestSchema` HAS, AS A DISCRIMINATED
 * UNION.** That schema expresses *"either a template or a shape"* with a refinement over optional
 * keys, because it was written before this one and its form fills both halves; a wizard step picks
 * a source first, so the discriminator is the honest shape here and the server maps one onto the
 * other in a line.
 *
 * ⚠️ **`null` means *write no assignment*, and that is what somebody who does not track time
 * gets.** The mockup's `obFinish` writes a schedule only `if (d.tracks)` — a person with no hours
 * to place has nothing for a daily plan to be measured against, and inventing one would put them
 * in the capacity reports they were just excluded from.
 */
exports.onboardingScheduleSchema = zod_1.z.discriminatedUnion('source', [
    zod_1.z.object({
        source: zod_1.z.literal('template'),
        fromDate: wireDateSchema,
        templateId: zod_1.z.uuid(),
    }),
    zod_1.z.object({
        source: zod_1.z.literal('custom'),
        fromDate: wireDateSchema,
        type: zod_1.z.enum(['fix', 'flex']),
        /** ⚠️ Seven, Monday first — `work_days_ok` says the same thing in the database. */
        days: zod_1.z.array(zod_1.z.number().min(0).max(24)).length(7),
        weeklyHours: zod_1.z.number().min(0).max(168),
    }),
]);
/**
 * C64 · the `leave` step's quota rows.
 *
 * ⚠️ **Only the types that draw on a quota, and only the ones somebody typed a figure into.** An
 * unlimited type has no entitlement to set, and a row of `0` written for every type would turn
 * *"nobody has decided yet"* into *"they get none"* — which the request screen would then enforce.
 */
exports.onboardingQuotaSchema = zod_1.z.object({
    leaveTypeId: zod_1.z.uuid(),
    days: zod_1.z.number().int().min(0).max(365),
});
/** C54 · one skill at one level — the same pair `setMemberSkillRequestSchema` carries. */
exports.onboardingSkillSchema = zod_1.z.object({
    skillId: zod_1.z.uuid(),
    levelId: zod_1.z.uuid(),
});
exports.completeOnboardingRequestSchema = zod_1.z.object({
    phone: zod_1.z.string().max(40).nullable(),
    employmentType: zod_1.z.enum(exports.EMPLOYMENT_TYPES),
    /** R-60 · ⚠️ Optional: absent leaves the person's teams as they are. */
    teamIds: teams_1.teamIdsSchema.optional(),
    managerMemberId: zod_1.z.uuid().nullable(),
    /**
     * 17 Oct 2026 · the `org` step's *Alap projekt szerep*. ⚠️ Optional as well as nullable: absent
     * leaves what the person already has, so a caller older than the field does not clear it.
     */
    defaultProjectRoleId: zod_1.z.uuid().nullable().optional(),
    location: zod_1.z.string().max(120).nullable(),
    timezone: zod_1.z.string().min(1).max(64),
    language: zod_1.z.enum(['hu', 'en']),
    tracksTime: zod_1.z.boolean(),
    /**
     * ux-pass-2 · the `time` step's second switch, *"Erőforrását tervezzük"* (phase 5 mockup,
     * `obPlan`). ⚠️ Always written, like `leaveManaged`: a column on the member with no third state.
     */
    resourcePlanned: zod_1.z.boolean(),
    startsOn: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    /* ⚠️ At most one — `setMemberRolesRequestSchema` above. */
    roleIds: zod_1.z.array(zod_1.z.uuid()).max(1),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    /**
     * C61 · the `rate` step's answer. ⚠️ **Required and nullable, never absent**, because *"skip"* is
     * a decision somebody made in the wizard and *"the client is older than the step"* is not — and
     * an optional key would make the two indistinguishable on the server.
     */
    rate: exports.onboardingRateSchema.nullable(),
    /** C64 · the `sched` step. `null` writes no assignment — see the schema's own note. */
    schedule: exports.onboardingScheduleSchema.nullable(),
    /**
     * C64 · the `leave` step's switch — *"Szabadságkezelés a rendszerben"*.
     *
     * ⚠️ **A column on the member, so it is always written**, unlike the two steps above it: every
     * person either is or is not administered here, and there is no third state for a wizard to
     * leave behind.
     */
    leaveManaged: zod_1.z.boolean(),
    /** ⚠️ Ignored when `leaveManaged` is false — somebody outside the system has no entitlement. */
    leaveQuotas: zod_1.z.array(exports.onboardingQuotaSchema),
    /**
     * C54 · the `skill` step.
     *
     * ⚠️⚠️ **IN THE BODY RATHER THAN A SECOND REQUEST, AND THE REASON IS THE TRANSACTION.**
     * The rate, the schedule and the quotas already land in one — finishing the wizard is one act,
     * and a second call afterwards could leave somebody priced and scheduled but with the four
     * skills their colleague just typed silently gone.
     *
     * ⚠️ **The WHOLE set, not a delta.** The step seeds from what the person already holds, so
     * what comes back is what they should hold: anything missing from it was removed on purpose.
     */
    skills: zod_1.z.array(exports.onboardingSkillSchema),
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * A bulk edit from the people list — item 72.
 *
 * ⚠️⚠️ **TEAM AND EMPLOYMENT TYPE, AND NOT THE REST OF A PERSON.** *"Move these six to Delivery"*
 * and *"these four are contractors"* are properties of a SELECTION; a name, a phone number or a
 * start date is not, and a bulk field that writes one person's fact onto six is a data-loss button
 * wearing a convenience label.
 *
 */
exports.bulkPersonEditSchema = zod_1.z.object({
    targets: bulk_1.bulkTargetsSchema,
    /* ⚠️ **The same bounds as `updatePersonRequestSchema`'s own fields, deliberately.** A bulk edit
       that accepted an 80-character team where the single-row form accepts 60 would be a second
       validation somebody has to keep in step — and the wider one always wins by accident. */
    /**
     * R-60 · ⚠️⚠️ **ADD OR REMOVE ONE TEAM, NEVER REPLACE THE SET.** A person can be in several
     * teams now, so *"move these six to Delivery"* became *"put these six in Delivery"* — writing one
     * set onto six people would take each of them out of every other team they were in.
     */
    addTeamId: zod_1.z.uuid().optional(),
    removeTeamId: zod_1.z.uuid().optional(),
    employmentType: zod_1.z.enum(exports.EMPLOYMENT_TYPES).optional(),
});
//# sourceMappingURL=contract.js.map